import type {
  App6Echelon,
  App6EchelonCode,
  App6Entry,
  App6Palette,
  Bilingual,
  OdbSide,
  OdbVocabulary,
} from "../../api/types";
import { lang, tc } from "../../i18n/i18n";
import FALLBACK from "./fixtures/app6_palette.json";
import { parseApp6 } from "./sidc";

/** Palette APP-6 de l'éditeur d'ordre de bataille : symboles terrestres OTAN proposés, échelons, qualificatifs
 *  (secteur 2). La référence est servie par le serveur avec le vocabulaire (`GET /odbs/vocabulary`, données
 *  versionnées server/generex_engine/data/app6_palette.json) ; un serveur antérieur qui ne la sert pas reçoit la
 *  doublure du client (fixtures/app6_palette.json, mêmes clés pour les fonctions courantes). */

export const FALLBACK_PALETTE = FALLBACK as unknown as App6Palette;

export function paletteOf(vocabulary: OdbVocabulary | null | undefined): App6Palette {
  return vocabulary?.app6 ?? FALLBACK_PALETTE;
}

/** Texte d'une donnée bilingue dans la langue de l'interface. */
export function bi(text: Bilingual | null | undefined): string {
  if (!text) return "";
  return lang() === "en" ? text.en || text.fr : text.fr;
}

/** Minuscules sans accents, pour une recherche tolérante. */
export function fold(text: string): string {
  return text
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();
}

/** Entrées proposées à un camp (Groupe armé et Population : ennemi seulement). */
export function entriesFor(palette: App6Palette, side: OdbSide): App6Entry[] {
  return palette.entries.filter((e) => e.sides.includes(side));
}

/** Recherche dans la palette : libellés français et anglais, mots-clés et code (« 1211 », « 121104 »), sans accents
 *  ni casse ; chaque mot doit trouver sa place. */
export function searchEntries(entries: readonly App6Entry[], query: string): App6Entry[] {
  const words = fold(query).split(/\s+/).filter(Boolean);
  if (!words.length) return [...entries];
  return entries.filter((entry) => {
    const haystack = fold([entry.fr, entry.en, ...entry.keywords, entry.app6.replace(/-/g, " "), entry.app6.replace(/-/g, "")].join(" "));
    // Orthographes -ise / -ize confondues (« motorised » trouve « Motorized infantry »).
    return words.every((word) => [word, word.replace(/iz/g, "is"), word.replace(/is/g, "iz")].some((v) => haystack.includes(v)));
  });
}

/** Entrée de la palette d'une unité, comme le serveur la résout : la clé si elle concorde avec le code (même
 *  ensemble, entité et modificateur secteur 1), sinon l'entrée de base du code, puis de l'entité sans
 *  modificateur, puis de sa famille ; null si rien ne correspond (fonction interarmes pour le moteur). */
export function entryFor(palette: App6Palette, fonction: string | null | undefined, app6: string | null | undefined): App6Entry | null {
  const parts = parseApp6(app6);
  const byKey = fonction ? palette.entries.find((e) => e.key === fonction) : undefined;
  if (byKey && !parts) return byKey;
  if (!parts) return null;
  const same = (e: App6Entry, entity: string, m1: string) => {
    const p = parseApp6(e.app6);
    return Boolean(p && p.set === parts.set && p.entity === entity && p.m1 === m1);
  };
  if (byKey && same(byKey, parts.entity, parts.m1)) return byKey;
  const exact = palette.entries.find((e) => e.app6 === app6);
  if (exact) return exact;
  const base = (entity: string, m1: string) =>
    palette.entries.find((e) => e.base && same(e, entity, m1)) ?? palette.entries.find((e) => same(e, entity, m1));
  return base(parts.entity, parts.m1) ?? base(parts.entity, "00") ?? base(`${parts.entity.slice(0, 4)}00`, "00") ?? null;
}

/** Libellé d'une fonction (palette), ou null si la palette ne la connaît pas. Le qualificatif (secteur 2) d'une
 *  entrée de base s'ajoute entre parenthèses : « Artillerie (Automoteur) ». */
export function functionLabel(palette: App6Palette, fonction: string | null | undefined, app6: string | null | undefined): string | null {
  const entry = entryFor(palette, fonction, app6);
  if (!entry) return null;
  const parts = parseApp6(app6);
  const own = parseApp6(entry.app6);
  if (parts && own && parts.m2 !== own.m2 && parts.m2 !== "00") {
    const qualifier = palette.qualifiers.find((q) => q.m2 === parts.m2);
    if (qualifier) return `${bi(entry)} (${bi(qualifier).toLowerCase()})`;
  }
  return bi(entry);
}

export function echelonInfo(palette: App6Palette, code: App6EchelonCode | null | undefined): App6Echelon | null {
  return palette.echelons.find((e) => e.code === code) ?? null;
}

/** Libellé court d'un échelon dans une colonne étroite (« Cie », « S/GTIA » en force opérationnelle ; « Coy »,
 *  « Coy tm » en anglais). */
export function echelonShort(code: App6EchelonCode | null | undefined, tf = false): string {
  switch (code) {
    case "11":
      return tc("abrégé", "Équipe");
    case "12":
      return tc("abrégé", "Groupe");
    case "13":
      return tc("abrégé", "Sion OTAN");
    case "14":
      return tc("abrégé", "Section");
    case "15":
      return tf ? tc("abrégé", "S/GTIA") : tc("abrégé", "Cie");
    case "16":
      return tf ? tc("abrégé", "GTIA") : tc("abrégé", "Bataillon");
    case "17":
      return tf ? tc("abrégé", "GTIA") : tc("abrégé", "Régiment");
    case "18":
      return tc("abrégé", "Brigade");
    case "21":
      return tc("abrégé", "Division");
    case "00":
      return tc("abrégé", "Irrégulier");
    default:
      return "";
  }
}

/** Libellé d'une nouvelle unité : « Compagnie 2 », « Company 2 » (nom de l'échelon dans la langue de l'interface,
 *  avant la barre oblique). */
export function echelonUnitName(palette: App6Palette, code: App6EchelonCode, number: string | null): string {
  const label = bi(echelonInfo(palette, code)).split(/\s*[/(]/)[0]?.trim() ?? "";
  return number ? `${label} ${number}` : label;
}

// --- Récemment utilisés (commodité par poste) --------------------------------------------------------------------

const RECENT_KEY = "generex-odb-app6-recents";
const RECENT_MAX = 8;

export function recentKeys(): string[] {
  try {
    const value = JSON.parse(localStorage.getItem(RECENT_KEY) ?? "[]") as unknown;
    return Array.isArray(value) ? value.filter((v): v is string => typeof v === "string").slice(0, RECENT_MAX) : [];
  } catch {
    return []; // stockage refusé ou illisible : la palette fonctionne sans
  }
}

export function rememberRecent(key: string): string[] {
  const next = [key, ...recentKeys().filter((k) => k !== key)].slice(0, RECENT_MAX);
  try {
    localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  } catch {
    // stockage refusé : rien n'est retenu
  }
  return next;
}
